import { FC, useEffect, useState } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'

import { Box } from '@factorialco/deprecated-design-system'
import { Button } from '@factorialco/factorial-one-react'
import { Add } from '@factorialco/factorial-one-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import { Element } from 'modules/forms/lib/Element'

import { ChoiceElement } from './ChoiceElement'

interface ChoiceElementEditorProps {
  element: Element
  onChange: (element: Element) => void
}

/**
 * Editor component for SingleChoiceElement & MultipleChoiceElement
 */
const ChoiceElementEditor: FC<ChoiceElementEditorProps> = ({ element, onChange }) => {
  const [options, setOptions] = useState([...element.options])

  useEffect(() => {
    setOptions([...element.options])
  }, [element, element.options])

  const moveOption = (fromIndex: number, toIndex: number) => {
    const newOptions = [...options]
    const removed = newOptions.splice(fromIndex, 1)
    if (removed.length !== 1 || !removed[0]) return
    newOptions.splice(toIndex, 0, removed[0])
    setOptions(newOptions)
  }

  const restartInitialValues = () => {
    setOptions(element.options)
  }

  const handleElementOptionChange = (index: number, label: string) => {
    if (index >= 0 && index < element.options.length && element.options[index]) {
      element.options[index].label = label
      onChange(element)
    }
  }

  const handleRemoveOption = (index: number) => {
    element.options.splice(index, 1)
    onChange(element)
  }

  const onDrop = (fromIndex: number, toIndex: number) => {
    const [movedOption] = element.options.splice(fromIndex, 1)
    if (movedOption) {
      element.options.splice(toIndex, 0, movedOption)
      onChange(element)
    }
  }

  const addChoiceElement = async () => {
    const newOption = {
      label: `Option ${element.options.length + 1}`,
      value: ``,
      id: '',
    }
    element.addOption(newOption)
    onChange(element)
  }

  return (
    <>
      <DndProvider backend={HTML5Backend}>
        {options.map((option, index) => {
          return (
            <ChoiceElement
              key={option.id}
              option={option}
              index={index}
              handleRemoveOption={handleRemoveOption}
              handleUpdateOption={handleElementOptionChange}
              restartInitialValues={restartInitialValues}
              moveElement={moveOption}
              onDrop={onDrop}
            />
          )
        })}
      </DndProvider>
      <Box flexDirection="row">
        <Button
          label={i18n.t('forms.editor.add_option_button')}
          icon={Add}
          onClick={addChoiceElement}
          variant="ghost"
        />
      </Box>
    </>
  )
}

export default ChoiceElementEditor
